import { useState, useEffect, useCallback } from 'react';
import {
  Server,
  Activity,
  AlertTriangle,
  CheckCircle,
  XCircle,
  RefreshCw,
  Cloud,
  Database,
  Zap,
  Clock,
  ExternalLink,
  AlertOctagon,
  Moon,
} from 'lucide-react';
import type {
  MonitoringStatus,
  ProjectsResult,
  ScanResults,
  ServiceProjects,
  LogError,
} from '../types/monitoring';
import './Insights.css';

// ============================================================================
// PROVIDER DISPLAY CONFIG
// ============================================================================

const providerConfig = {
  vercel: {
    name: 'Vercel',
    icon: Cloud,
    color: 'blue',
    description: 'Frontend & Serverless',
  },
  railway: {
    name: 'Railway',
    icon: Server,
    color: 'purple',
    description: 'Backend Services',
  },
  supabase: {
    name: 'Supabase',
    icon: Database,
    color: 'green',
    description: 'Database & Edge Functions',
  },
};

// ============================================================================
// STATUS HELPERS
// ============================================================================

function getStatusIcon(status: string) {
  const s = status?.toUpperCase() || '';
  if (s.includes('READY') || s.includes('ACTIVE') || s.includes('HEALTHY')) {
    return { icon: CheckCircle, className: 'status-healthy' };
  }
  if (s.includes('CRASHED') || s.includes('FAILED') || s.includes('ERROR')) {
    return { icon: XCircle, className: 'status-error' };
  }
  if (s.includes('SLEEPING')) {
    return { icon: Moon, className: 'status-sleeping' };
  }
  if (s.includes('BUILDING') || s.includes('DEPLOYING') || s.includes('QUEUED') || s.includes('INITIALIZING')) {
    return { icon: RefreshCw, className: 'status-building' };
  }
  return { icon: AlertTriangle, className: 'status-unknown' };
}

function getErrorLevelClass(level: string) {
  switch (level) {
    case 'critical':
      return 'error-critical';
    case 'error':
      return 'error-error';
    case 'warning':
      return 'error-warning';
    default:
      return 'error-info';
  }
}

function formatTimeAgo(dateString: string) {
  const date = new Date(dateString);
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const minutes = Math.floor(diff / (1000 * 60));
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  if (minutes > 0) return `${minutes}m ago`;
  return 'Just now';
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function Insights() {
  const [status, setStatus] = useState<MonitoringStatus | null>(null);
  const [projects, setProjects] = useState<ProjectsResult | null>(null);
  const [scanResults, setScanResults] = useState<ScanResults | null>(null);
  const [loading, setLoading] = useState({ status: true, projects: true, scan: false });
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  // Fetch monitoring status
  const fetchStatus = useCallback(async () => {
    try {
      const res = await fetch('/api/monitoring/status');
      if (res.ok) {
        const data = await res.json();
        setStatus(data);
      }
    } catch (err) {
      console.error('Error fetching status:', err);
    } finally {
      setLoading((prev) => ({ ...prev, status: false }));
    }
  }, []);

  // Fetch projects
  const fetchProjects = useCallback(async () => {
    setLoading((prev) => ({ ...prev, projects: true }));
    try {
      const res = await fetch('/api/monitoring/projects');
      if (res.ok) {
        const data = await res.json();
        setProjects(data);
        setLastRefresh(new Date());
      }
    } catch (err) {
      console.error('Error fetching projects:', err);
      setError('Failed to fetch projects');
    } finally {
      setLoading((prev) => ({ ...prev, projects: false }));
    }
  }, []);

  // Scan logs
  const scanLogs = useCallback(async () => {
    setLoading((prev) => ({ ...prev, scan: true }));
    setError(null);
    try {
      const res = await fetch('/api/monitoring/scan-logs', { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        setScanResults(data);
        setLastRefresh(new Date());
      } else {
        setError('Failed to scan logs');
      }
    } catch (err) {
      console.error('Error scanning logs:', err);
      setError('Failed to scan logs');
    } finally {
      setLoading((prev) => ({ ...prev, scan: false }));
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchStatus();
    fetchProjects();
  }, [fetchStatus, fetchProjects]);

  // Refresh all data
  const refreshAll = async () => {
    await Promise.all([fetchProjects(), scanLogs()]);
  };

  // Calculate totals
  const totalProjects = projects?.services.reduce((sum, s) => sum + s.projects.length, 0) || 0;
  const healthyProjects = projects?.services.reduce((sum, s) => {
    return sum + s.projects.filter((p) => {
      const status = p.status?.toUpperCase() || '';
      return status.includes('READY') || status.includes('ACTIVE') || status.includes('HEALTHY');
    }).length;
  }, 0) || 0;
  const errorProjects = projects?.services.reduce((sum, s) => {
    return sum + s.projects.filter((p) => {
      const status = p.status?.toUpperCase() || '';
      return status.includes('CRASHED') || status.includes('FAILED') || status.includes('ERROR');
    }).length;
  }, 0) || 0;

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>Command Insights</h1>
          <p>Monitor infrastructure across Vercel, Railway, and Supabase</p>
        </div>
        <div className="header-actions">
          {lastRefresh && (
            <span className="last-refresh">
              <Clock size={14} />
              Updated {formatTimeAgo(lastRefresh.toISOString())}
            </span>
          )}
          <button
            className="btn btn-primary"
            onClick={refreshAll}
            disabled={loading.projects || loading.scan}
          >
            <RefreshCw size={16} className={loading.projects || loading.scan ? 'spin' : ''} />
            Refresh All
          </button>
        </div>
      </div>

      {error && (
        <div className="error-banner">
          <AlertTriangle size={16} />
          {error}
        </div>
      )}

      {/* Integration Status Cards */}
      <div className="grid grid-3 integration-grid">
        {(['vercel', 'railway', 'supabase'] as const).map((provider) => {
          const config = providerConfig[provider];
          const Icon = config.icon;
          const isConfigured = status?.integrations[provider] ?? false;
          const serviceData = projects?.services.find((s) => s.source === provider);
          const projectCount = serviceData?.projects.length || 0;
          const healthyCount = serviceData?.projects.filter((p) => {
            const s = p.status?.toUpperCase() || '';
            return s.includes('READY') || s.includes('ACTIVE') || s.includes('HEALTHY');
          }).length || 0;

          return (
            <div key={provider} className={`integration-card ${config.color} ${!isConfigured ? 'disabled' : ''}`}>
              <div className="integration-header">
                <div className={`integration-icon ${config.color}`}>
                  <Icon size={24} />
                </div>
                <div className="integration-status">
                  {isConfigured ? (
                    <span className="badge badge-passing">Connected</span>
                  ) : (
                    <span className="badge badge-muted">Not Configured</span>
                  )}
                </div>
              </div>
              <h3 className="integration-name">{config.name}</h3>
              <p className="integration-description">{config.description}</p>
              {isConfigured && (
                <div className="integration-stats">
                  <div className="integration-stat">
                    <span className="stat-number">{projectCount}</span>
                    <span className="stat-label">Projects</span>
                  </div>
                  <div className="integration-stat">
                    <span className="stat-number healthy">{healthyCount}</span>
                    <span className="stat-label">Healthy</span>
                  </div>
                  {projectCount - healthyCount > 0 && (
                    <div className="integration-stat">
                      <span className="stat-number warning">{projectCount - healthyCount}</span>
                      <span className="stat-label">Issues</span>
                    </div>
                  )}
                </div>
              )}
              {serviceData?.message && (
                <p className="integration-message">{serviceData.message}</p>
              )}
            </div>
          );
        })}
      </div>

      {/* Overview Stats */}
      <div className="grid grid-4 stats-grid">
        <div className="stat-card">
          <div className="stat-icon blue">
            <Server size={24} />
          </div>
          <div className="stat-content">
            <span className="stat-value">{totalProjects}</span>
            <span className="stat-label">Total Projects</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon green">
            <CheckCircle size={24} />
          </div>
          <div className="stat-content">
            <span className="stat-value">{healthyProjects}</span>
            <span className="stat-label">Healthy</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon red">
            <XCircle size={24} />
          </div>
          <div className="stat-content">
            <span className="stat-value">{errorProjects}</span>
            <span className="stat-label">Errors</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon yellow">
            <AlertTriangle size={24} />
          </div>
          <div className="stat-content">
            <span className="stat-value">{scanResults?.totalErrors ?? '-'}</span>
            <span className="stat-label">Log Errors</span>
          </div>
          {!scanResults && (
            <button className="btn btn-sm btn-secondary" onClick={scanLogs} disabled={loading.scan}>
              Scan
            </button>
          )}
        </div>
      </div>

      {/* Projects & Errors Grid */}
      <div className="grid grid-2 insights-main">
        {/* Projects List */}
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">
              <Server size={20} />
              Projects & Services
            </h2>
            <button
              className="btn btn-secondary btn-sm"
              onClick={fetchProjects}
              disabled={loading.projects}
            >
              <RefreshCw size={14} className={loading.projects ? 'spin' : ''} />
            </button>
          </div>

          {loading.projects && !projects ? (
            <div className="loading-placeholder">Loading projects...</div>
          ) : (
            <div className="projects-list">
              {projects?.services.map((service: ServiceProjects) => {
                const config = providerConfig[service.source];
                const Icon = config.icon;

                return (
                  <div key={service.source} className="provider-section">
                    <div className="provider-header">
                      <Icon size={16} />
                      <span className="provider-name">{config.name}</span>
                      {!service.configured && (
                        <span className="badge badge-muted">Not Configured</span>
                      )}
                      {service.message && (
                        <span className="provider-message">{service.message}</span>
                      )}
                    </div>

                    {service.configured && service.projects.length > 0 && (
                      <div className="project-items">
                        {service.projects.slice(0, 10).map((project) => {
                          const { icon: StatusIcon, className } = getStatusIcon(project.status || '');
                          return (
                            <div key={project.id} className="project-item">
                              <StatusIcon size={16} className={className} />
                              <span className="project-name">{project.name}</span>
                              <span className={`project-status ${className}`}>
                                {project.status || 'unknown'}
                              </span>
                              {project.url && (
                                <a
                                  href={project.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="project-link"
                                >
                                  <ExternalLink size={12} />
                                </a>
                              )}
                            </div>
                          );
                        })}
                        {service.projects.length > 10 && (
                          <div className="projects-more">
                            +{service.projects.length - 10} more projects
                          </div>
                        )}
                      </div>
                    )}

                    {service.configured && service.projects.length === 0 && (
                      <p className="no-projects">No projects found</p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Error Log Scanner */}
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">
              <AlertOctagon size={20} />
              Error Log Scanner
            </h2>
            <button
              className="btn btn-primary btn-sm"
              onClick={scanLogs}
              disabled={loading.scan}
            >
              <Zap size={14} />
              {loading.scan ? 'Scanning...' : 'Scan Now'}
            </button>
          </div>

          {loading.scan && (
            <div className="scan-progress">
              <RefreshCw size={24} className="spin" />
              <p>Scanning service logs for errors...</p>
              <p className="scan-note">Checking Vercel, Railway, and Supabase</p>
            </div>
          )}

          {!loading.scan && !scanResults && (
            <div className="scan-placeholder">
              <AlertTriangle size={48} className="placeholder-icon" />
              <h3>No Scan Results</h3>
              <p>Click "Scan Now" to check for errors across all services</p>
              <p className="scan-info">Scans deployment errors, runtime errors, and service health issues from the past hour</p>
            </div>
          )}

          {!loading.scan && scanResults && (
            <div className="scan-results">
              <div className="scan-summary">
                <div className={`scan-total ${scanResults.totalErrors === 0 ? 'healthy' : 'errors'}`}>
                  {scanResults.totalErrors === 0 ? (
                    <>
                      <CheckCircle size={20} />
                      <span>All Clear - No errors found</span>
                    </>
                  ) : (
                    <>
                      <AlertTriangle size={20} />
                      <span>{scanResults.totalErrors} error{scanResults.totalErrors > 1 ? 's' : ''} found</span>
                    </>
                  )}
                </div>
                <span className="scan-time">
                  Scanned at {new Date(scanResults.timestamp).toLocaleTimeString()}
                </span>
              </div>

              {/* Per-service breakdown */}
              <div className="scan-services">
                {scanResults.results.map((result) => {
                  const config = providerConfig[result.source as keyof typeof providerConfig];
                  const Icon = config?.icon || Server;

                  return (
                    <div key={result.source} className="scan-service">
                      <div className="scan-service-header">
                        <Icon size={16} />
                        <span className="service-name">{result.source.toUpperCase()}</span>
                        {!result.scanned ? (
                          <span className="badge badge-muted">Not Configured</span>
                        ) : result.errorCount === 0 ? (
                          <span className="badge badge-passing">No Errors</span>
                        ) : (
                          <span className="badge badge-failing">{result.errorCount} Error{result.errorCount > 1 ? 's' : ''}</span>
                        )}
                      </div>

                      {result.scanned && result.errors.length > 0 && (
                        <div className="error-list">
                          {result.errors.slice(0, 5).map((error: LogError, idx: number) => (
                            <div key={idx} className={`error-item ${getErrorLevelClass(error.level)}`}>
                              <div className="error-header">
                                <span className={`error-level ${error.level}`}>{error.level}</span>
                                <span className="error-time">{formatTimeAgo(error.timestamp)}</span>
                              </div>
                              <div className="error-service">{error.service}</div>
                              <div className="error-message">{error.message}</div>
                            </div>
                          ))}
                          {result.errors.length > 5 && (
                            <div className="errors-more">
                              +{result.errors.length - 5} more errors
                            </div>
                          )}
                        </div>
                      )}

                      {result.message && (
                        <p className="scan-service-message">{result.message}</p>
                      )}
                    </div>
                  );
                })}
              </div>

              {scanResults.notificationsSent > 0 && (
                <div className="notifications-sent">
                  <Activity size={14} />
                  {scanResults.notificationsSent} notification{scanResults.notificationsSent > 1 ? 's' : ''} sent to Discord #incidents
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Available Data Info */}
      <div className="card info-card">
        <div className="card-header">
          <h2 className="card-title">
            <Zap size={20} />
            Available Insights
          </h2>
        </div>
        <div className="insights-info">
          <div className="info-section">
            <h4>
              <Cloud size={16} />
              Vercel
            </h4>
            <ul>
              <li>Project listing with deployment status</li>
              <li>Failed deployment detection</li>
              <li>Runtime error log scanning</li>
              <li>Domain & environment variable management (via MCP)</li>
            </ul>
          </div>
          <div className="info-section">
            <h4>
              <Server size={16} />
              Railway
            </h4>
            <ul>
              <li>Projects & services with health status</li>
              <li>Crashed/failed service detection</li>
              <li>Sleeping service notifications</li>
              <li>Deployment history & logs (via MCP)</li>
            </ul>
          </div>
          <div className="info-section">
            <h4>
              <Database size={16} />
              Supabase
            </h4>
            <ul>
              <li>Project health monitoring</li>
              <li>Edge Function status & errors</li>
              <li>Function log scanning</li>
              <li>Database & auth user management (via MCP)</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
